import React, { useEffect, useState, useCallback } from 'react';
import { optImg } from '../lib/img';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Trophy, Play, ChevronRight, Calendar, Sun, Sparkles, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LeagueShowcase from '../components/LeagueShowcase';
import SponsorCarousel from '../components/SponsorCarousel';
import AchievementCarousel from '../components/AchievementCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Professional cricket images - defaults (overridden by API)
const DEFAULT_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1753791401265-d4c8b77c4f34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjcmlja2V0JTIwc3RhZGl1bSUyMHRlc3QlMjBtYXRjaCUyMGRheSUyMGdhbWUlMjBncmVlbiUyMGZpZWxkfGVufDB8fHx8MTc3Mzc2MDc5Mnww&ixlib=rb-4.1.0&q=85',
  stadium: 'https://images.unsplash.com/photo-1765915550935-578b594ac76b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxjcmlja2V0JTIwc3RhZGl1bSUyMHRlc3QlMjBtYXRjaCUyMGRheSUyMGdhbWUlMjBncmVlbiUyMGZpZWxkfGVufDB8fHx8MTc3Mzc2MDc5Mnww&ixlib=rb-4.1.0&q=85',
  nets: 'https://images.unsplash.com/photo-1521062764155-3a1373fb115c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjcmlja2V0JTIwYWNhZGVteSUyMGNvYWNoaW5nJTIwbmV0cyUyMHByYWN0aWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzc2MDg4MHww&ixlib=rb-4.1.0&q=85',
  about_section: 'https://images.unsplash.com/photo-1771909713927-1035f4cabb7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxjcmlja2V0JTIwYWNhZGVteSUyMGNvYWNoaW5nJTIwbmV0cyUyMHByYWN0aWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzc2MDg4MHww&ixlib=rb-4.1.0&q=85',
  bats: 'https://images.unsplash.com/photo-1713797207049-dabd02c70a39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwYWNhZGVteSUyMGNvYWNoaW5nJTIwbmV0cyUyMHByYWN0aWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzc2MDg4MHww&ixlib=rb-4.1.0&q=85',
  batsman: 'https://images.unsplash.com/photo-1659081476962-df97acad7f1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxjcmlja2V0JTIwaGVsbWV0JTIwcGFkcyUyMGdsb3ZlcyUyMHByb2Zlc3Npb25hbCUyMGdlYXJ8ZW58MHx8fHwxNzczNzYwNzg2fDA&ixlib=rb-4.1.0&q=85',
  ball: 'https://images.unsplash.com/photo-1587385789097-0197a7fbd179?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwYmFsbCUyMGJhdCUyMHN0dW1wcyUyMHdpY2tldCUyMHJlZCUyMGxlYXRoZXJ8ZW58MHx8fHwxNzczNzYwNzk4fDA&ixlib=rb-4.1.0&q=85',
  keeper: 'https://images.unsplash.com/photo-1743342398244-c8c5a307d290?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwaGVsbWV0JTIwcGFkcyUyMGdsb3ZlcyUyMHByb2Zlc3Npb25hbCUyMGdlYXJ8ZW58MHx8fHwxNzczNzYwNzg2fDA&ixlib=rb-4.1.0&q=85',
  video_default: 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&h=450&fit=crop'
};

const Home = () => {
  const [featuredCamps, setFeaturedCamps] = useState([]);
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [playerOfMonth, setPlayerOfMonth] = useState(null);
  const [potmEnabled, setPotmEnabled] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsSectionEnabled, setTestimonialsSectionEnabled] = useState(true);
  const [heroContent, setHeroContent] = useState(null);
  const [siteImages, setSiteImages] = useState(DEFAULT_IMAGES);
  const [homeBanner, setHomeBanner] = useState(null);

  const fetchFeaturedCamps = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/camps/featured`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedCamps(data.camps || []);
      }
    } catch (err) {
      console.error('Failed to fetch camps:', err);
    }
  }, []);

  const fetchTrainingVideos = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos`);
      if (response.ok) {
        const data = await response.json();
        setTrainingVideos(data.videos || []);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    }
  }, []);

  const fetchSponsors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/sponsors`);
      if (response.ok) {
        const data = await response.json();
        // Map API response to component expected format
        const mappedSponsors = (data.sponsors || []).map(s => ({
          id: s.sponsor_id,
          name: s.name,
          logo: s.logo_url,
          url: s.website_url
        }));
        setSponsors(mappedSponsors);
      }
    } catch (err) {
      console.error('Failed to fetch sponsors:', err);
    }
  }, []);

  const fetchLeagues = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/leagues`);
      if (response.ok) {
        const data = await response.json();
        // Map API response to component expected format
        const mappedLeagues = (data.leagues || []).map(l => ({
          id: l.league_id,
          name: l.name,
          description: l.description,
          type: l.league_type,
          ageGroups: l.age_groups,
          icon: l.icon
        }));
        setLeagues(mappedLeagues);
      }
    } catch (err) {
      console.error('Failed to fetch leagues:', err);
    }
  }, []);

  const fetchPlayerOfMonth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/player-of-month`);
      if (response.ok) {
        const data = await response.json();
        setPotmEnabled(data.enabled);
        setPlayerOfMonth(data.player);
      }
    } catch (err) {
      console.error('Failed to fetch player of month:', err);
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      // Fetch list + section enable-state in parallel; if section is disabled,
      // we suppress the entire block client-side regardless of list size.
      const [listRes, stateRes] = await Promise.all([
        fetch(`${API_URL}/api/testimonials`),
        fetch(`${API_URL}/api/testimonials/section-state`),
      ]);
      if (listRes.ok) {
        const data = await listRes.json();
        setTestimonials(data.testimonials || []);
      }
      if (stateRes.ok) {
        const state = await stateRes.json();
        setTestimonialsSectionEnabled(state.enabled !== false);
      }
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
    }
  }, []);

  const fetchHeroContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/hero`);
      if (response.ok) {
        setHeroContent(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch hero content:', err);
    }
  }, []);

  const fetchHomeBanner = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/home-banner`);
      if (response.ok) {
        setHomeBanner(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch home banner:', err);
    }
  }, []);

  const fetchHomeImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/home-images`);
      if (response.ok) {
        const data = await response.json();
        setSiteImages(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to fetch home images:', err);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchFeaturedCamps();
    fetchTrainingVideos();
    fetchSponsors();
    fetchLeagues();
    fetchPlayerOfMonth();
    fetchTestimonials();
    fetchHeroContent();
    fetchHomeImages();
    fetchHomeBanner();
  }, [fetchFeaturedCamps, fetchTrainingVideos, fetchSponsors, fetchLeagues, fetchPlayerOfMonth, fetchTestimonials, fetchHeroContent, fetchHomeImages, fetchHomeBanner]);

  // Hero defaults (used while loading or if API fails)
  const hero = heroContent || {
    badge_text: "Town of Apex • Town of Cary • CDA Family",
    heading_line1: "From Schoolyards to Stadiums —",
    heading_highlight: "Cricket Drive.",
    paragraph1: "The Triangle's only full-journey cricket program, rooted in Wake, Durham & Chatham County schools and proudly affiliated with the Town of Apex and Town of Cary.",
    paragraph2: "Whether your child is picking up a bat for the first time or training for USA Cricket Nationals — we coach every age, every stage, with no long-term contracts and genuine family commitment.",
    cta_text: "Join the CDA Family",
    stat1_value: "100+", stat1_label: "Students Trained",
    stat2_value: "3", stat2_label: "Counties Served",
    stat3_value: "U6-U19", stat3_label: "All Age Groups",
    background_video_url: "",
    background_image_url: ""
  };

  return (
    <div className="min-h-screen">
      {/* Summer Camps 2026 Banner — top-of-page notice so it's visible to
          every visitor before they scroll. Editable from Admin → Programs
          tab → Home Page Promo Banner. Hidden entirely when enabled=false. */}
      {homeBanner?.enabled && (
        (() => {
          const themes = {
            orange: 'from-yellow-400 via-orange-400 to-yellow-500',
            blue:   'from-blue-400 via-blue-500 to-blue-600',
            green:  'from-green-400 via-green-500 to-green-600',
            red:    'from-red-400 via-red-500 to-red-600',
          };
          const themeClass = themes[homeBanner.color_theme] || themes.orange;
          const isExternal = (homeBanner.cta_link || '').startsWith('http');
          const CtaWrap = isExternal
            ? ({ children }) => <a href={homeBanner.cta_link} target="_blank" rel="noopener noreferrer">{children}</a>
            : ({ children }) => <Link to={homeBanner.cta_link || '/'}>{children}</Link>;
          return (
            <section className={`py-6 bg-gradient-to-r ${themeClass} relative overflow-hidden`} data-testid="top-summer-camps-banner">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(0,0,0,0.1) 20px, rgba(0,0,0,0.1) 40px)'
                }}></div>
              </div>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sun className="w-7 h-7 text-yellow-300" />
                    </div>
                    <div className="text-blue-900">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <h3 className="text-xl md:text-2xl font-bold">{homeBanner.headline}</h3>
                        {homeBanner.badge_text && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">{homeBanner.badge_text}</span>
                        )}
                      </div>
                      <p className="text-sm md:text-base font-medium">{homeBanner.subline}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    {homeBanner.price_text && (
                      <p className="text-blue-900 font-semibold text-base md:text-lg">{homeBanner.price_text}</p>
                    )}
                    <CtaWrap>
                      <Button className="bg-blue-900 hover:bg-blue-800 text-white font-bold px-6 py-2.5 text-base">
                        {homeBanner.cta_text}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </CtaWrap>
                  </div>
                </div>
              </div>
            </section>
          );
        })()
      )}

      {/* Hero Section with Video Background */}
      <section className="relative min-h-[90vh] text-white overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          {hero.background_video_url ? (
            // `key` forces React to remount the <video> element when the URL
            // changes — without this, swapping the <source> src after the
            // initial fetch doesn't trigger a fresh load and the video stays
            // stuck on the initial default URL (HTML5 spec quirk).
            <video
              key={hero.background_video_url}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              poster={optImg(hero.background_image_url || siteImages.hero, 1200)}
              src={hero.background_video_url}
            />
          ) : hero.background_image_url ? (
            <img src={optImg(hero.background_image_url, 1600)} alt="" fetchPriority="high" className="w-full h-full object-cover" />
          ) : (
            <img src={optImg(siteImages.hero, 1600)} alt="" fetchPriority="high" className="w-full h-full object-cover" />
          )}
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-blue-900/80 to-slate-900/70"></div>
        </div>

        {/* Pattern overlay */}
        <div className="absolute inset-0 z-[1] opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-green-500/20 px-4 py-2 rounded-full border border-green-500/30 mb-4 backdrop-blur-sm">
                <span className="w-2 h-2 bg-lime-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium text-lime-300">{hero.badge_text}</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                {hero.heading_line1}
                <span className="block mt-2 bg-gradient-to-r from-green-400 via-lime-400 to-orange-400 bg-clip-text text-transparent">
                  {hero.heading_highlight}
                </span>
              </h1>
              
              <p className="text-xl text-gray-200 leading-relaxed">
                {hero.paragraph1}
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                {hero.paragraph2}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/programs">
                  <Button size="lg" className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg group shadow-lg shadow-green-500/30" data-testid="hero-cta-btn">
                    <span className="mr-2">🏏</span>
                    {hero.cta_text}
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg backdrop-blur-sm">
                    Learn More
                  </Button>
                </Link>
              </div>

              <div className="flex items-center space-x-8 pt-4">
                <div className="backdrop-blur-sm bg-white/5 rounded-lg p-3">
                  <div className="text-3xl font-bold text-green-400">{hero.stat1_value}</div>
                  <div className="text-sm text-gray-300">{hero.stat1_label}</div>
                </div>
                <div className="w-px h-12 bg-gray-500"></div>
                <div className="backdrop-blur-sm bg-white/5 rounded-lg p-3">
                  <div className="text-3xl font-bold text-orange-400">{hero.stat2_value}</div>
                  <div className="text-sm text-gray-300">{hero.stat2_label}</div>
                </div>
                <div className="w-px h-12 bg-gray-500"></div>
                <div className="backdrop-blur-sm bg-white/5 rounded-lg p-3">
                  <div className="text-3xl font-bold text-lime-400">{hero.stat3_value}</div>
                  <div className="text-sm text-gray-300">{hero.stat3_label}</div>
                </div>
              </div>
            </div>
            
            {/* Player Achievement Carousel */}
            <div className="hidden lg:block relative">
              <AchievementCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* Summer Camps 2026 Banner — moved to top-of-page (above hero). */}

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={optImg(siteImages.about_section, 1000)}
                alt="Cricket Academy"
                loading="lazy"
                decoding="async"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-green-600 to-green-700 text-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">2019</div>
                <div className="text-sm">Established</div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-sm font-semibold text-green-600 bg-green-50 px-4 py-2 rounded-full">CRICKET DRIVE</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Unleashing Expert Solutions for Team Success
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                In the expansive journey of Cricket Drive Academy, beyond borders and diverse fields, aspiring talents unfold stories, nurtured by dedicated coaches. In the dynamic realm of cricket, the pursuit of excellence is not merely a journey but a strategic drive.
              </p>
              <blockquote className="border-l-4 border-green-600 pl-6 italic text-gray-700">
                "Give yourself the power of responsibility. Remind yourself the only thing stopping you is yourself."
              </blockquote>
              <Link to="/about">
                <Button className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white group">
                  Read More
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Player of the Month Section (above Junior Pathway, only when enabled) */}
      {potmEnabled && (
        <section className="py-16 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50" data-testid="player-of-month-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center space-x-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                <span>Player of the Month</span>
              </span>
              <h2 className="text-3xl font-bold text-gray-900">{playerOfMonth?.month || 'Coming Soon'}</h2>
            </div>
            {playerOfMonth ? (
              <Card className="border-none shadow-2xl overflow-hidden bg-white">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-64 md:h-auto">
                    <img
                      src={optImg(playerOfMonth.image_url || siteImages.batsman, 800)}
                      alt={playerOfMonth.player_name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent md:bg-gradient-to-r md:from-transparent md:to-transparent" />
                  </div>
                  <CardContent className="p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900" data-testid="potm-name">{playerOfMonth.player_name}</h3>
                        {playerOfMonth.age && <p className="text-sm text-gray-500">Age {playerOfMonth.age}</p>}
                      </div>
                    </div>
                    {playerOfMonth.program && (
                      <p className="text-sm font-medium text-green-600 mb-3">{playerOfMonth.program}</p>
                    )}
                    {playerOfMonth.achievement && (
                      <p className="text-gray-700 mb-4">{playerOfMonth.achievement}</p>
                    )}
                    {playerOfMonth.quote && (
                      <blockquote className="border-l-4 border-amber-400 pl-4 italic text-gray-600 text-sm">
                        "{playerOfMonth.quote}"
                      </blockquote>
                    )}
                  </CardContent>
                </div>
              </Card>
            ) : (
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-12 text-center">
                  <Trophy className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-700 mb-2">Player of the Month</h3>
                  <p className="text-gray-500">Check back soon — this month's star player will be announced shortly!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Summer & Track Out Camps Banner */}
      {/* Junior Pathway Teaser */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.3"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-green-500/20 px-4 py-2 rounded-full border border-green-500/30 mb-6">
              <span className="w-2 h-2 bg-lime-400 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-lime-300">Cricket Drive Junior Pathway</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              A Junior Pathway Like
              <span className="block mt-2 bg-gradient-to-r from-green-400 via-lime-400 to-orange-400 bg-clip-text text-transparent">
                No Other.
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              A skill-based development pathway — start with <strong className="text-green-400">Lil Masters</strong>, progress to <strong className="text-lime-300">Prime & Classic</strong>, and earn your place in the elite <strong className="text-orange-400">Emerging</strong> program. Every stage builds on the last.
            </p>
            
            <p className="text-lg text-gray-400 mb-8">
              The only academy in the Triangle with a full-spectrum, skill-driven pathway aligned with USA Cricket standards.
            </p>
            
            <Link to="/programs">
              <Button size="lg" className="bg-gradient-to-r from-green-500 to-lime-500 hover:from-green-600 hover:to-lime-600 text-white px-8 py-6 text-lg group shadow-lg shadow-green-500/30">
                Learn More About Our Programs
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            
            {/* Skill-based pathway overview */}
            <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-0" data-testid="pathway-overview">
              {/* Lil Masters */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-green-400/30 text-center w-full md:w-auto md:min-w-[160px]">
                <div className="text-2xl font-bold text-green-400 mb-1">Lil Masters</div>
                <div className="text-xs text-gray-400">Ages 7-11 · Skill Based</div>
                <div className="text-xs text-green-300 mt-1">Foundation</div>
              </div>
              {/* Arrow */}
              <div className="hidden md:flex items-center px-2">
                <div className="w-8 h-0.5 bg-gradient-to-r from-green-400 to-blue-400"></div>
                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-blue-400"></div>
              </div>
              <div className="md:hidden flex flex-col items-center py-1">
                <div className="h-6 w-0.5 bg-gradient-to-b from-green-400 to-blue-400"></div>
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-blue-400"></div>
              </div>
              {/* Prime */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-blue-400/30 text-center w-full md:w-auto md:min-w-[160px]">
                <div className="text-2xl font-bold text-blue-400 mb-1">Prime</div>
                <div className="text-xs text-gray-400">Until 11/12 yrs</div>
                <div className="text-xs text-blue-300 mt-1">Intermediate</div>
              </div>
              {/* Arrow */}
              <div className="hidden md:flex items-center px-2">
                <div className="w-8 h-0.5 bg-gradient-to-r from-blue-400 to-orange-400"></div>
                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-orange-400"></div>
              </div>
              <div className="md:hidden flex flex-col items-center py-1">
                <div className="h-6 w-0.5 bg-gradient-to-b from-blue-400 to-orange-400"></div>
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-orange-400"></div>
              </div>
              {/* Classic */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-orange-400/30 text-center w-full md:w-auto md:min-w-[160px]">
                <div className="text-2xl font-bold text-orange-400 mb-1">Classic</div>
                <div className="text-xs text-gray-400">Advanced Level</div>
                <div className="text-xs text-orange-300 mt-1">Advanced</div>
              </div>
              {/* Arrow */}
              <div className="hidden md:flex items-center px-2">
                <div className="w-8 h-0.5 bg-gradient-to-r from-orange-400 to-purple-400"></div>
                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-purple-400"></div>
              </div>
              <div className="md:hidden flex flex-col items-center py-1">
                <div className="h-6 w-0.5 bg-gradient-to-b from-orange-400 to-purple-400"></div>
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-purple-400"></div>
              </div>
              {/* Emerging */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-purple-400/30 text-center w-full md:w-auto md:min-w-[160px]">
                <div className="text-2xl font-bold text-purple-400 mb-1">Emerging</div>
                <div className="text-xs text-gray-400">Invite / Shortlist Only</div>
                <div className="text-xs text-purple-300 mt-1">High Performance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Leagues */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Competition Opportunities
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Leagues & Tournaments
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From regional competitions to professional pathways - multiple opportunities for competitive cricket
            </p>
          </div>

          <LeagueShowcase leagues={leagues} />
        </div>
      </section>

      {/* Training Videos Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">
                Training Videos
              </h2>
              <p className="text-gray-600">Learn from expert coaches with our comprehensive video library</p>
            </div>
            <Link to="/videos">
              <Button variant="outline" className="hidden sm:flex border-green-600 text-green-600 hover:bg-green-50">
                View All Videos
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {trainingVideos.slice(0, 3).map((video) => (
              <Card key={video.video_id || video.title} className="group cursor-pointer overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative overflow-hidden">
                  <img
                    src={optImg(video.thumbnail, 800) || 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&h=450&fit=crop'}
                    alt={video.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-green-600 ml-1" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {video.duration || '10:00'}
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      {video.category || 'General'}
                    </span>
                    <span className="text-xs text-gray-500">{video.level || 'All Levels'}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-sm text-gray-600">{video.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8 sm:hidden">
            <Link to="/videos">
              <Button className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white">
                View All Videos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials (above Our Partners) */}
      {testimonialsSectionEnabled && testimonials.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-green-50 to-lime-50" data-testid="testimonials-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
                What Parents & Players Say
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Real Stories from the CDA Family</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {testimonials.slice(0, 2).map((t) => (
                <Card
                  key={t.testimonial_id || t.name}
                  className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white"
                  data-testid={`home-testimonial-${t.testimonial_id || t.name}`}
                >
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-6">
                      <Quote className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex space-x-1 mb-4">
                      {[...Array(t.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6 text-lg line-clamp-5">
                      "{t.text}"
                    </p>
                    <div className="flex items-center space-x-4 pt-6 border-t border-gray-200">
                      <img
                        src={t.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name || 'CDA')}&background=22c55e&color=fff`}
                        alt={t.name}
                        loading="lazy"
                        decoding="async"
                        width="56"
                        height="56"
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-bold text-gray-900">{t.name}</h4>
                        <p className="text-sm text-green-600">{t.role || 'Parent'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {testimonials.length > 2 && (
              <div className="text-center mt-10">
                <Link to="/testimonials">
                  <Button variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="home-view-all-testimonials">
                    Read More Stories
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sponsors Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Our Partners
            </span>
            <h2 className="text-3xl font-bold text-gray-900">Proudly Supported By</h2>
          </div>
          <SponsorCarousel sponsors={sponsors} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 via-green-700 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            From Nets to Glory: Transforming Aspirations into Achievements!
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Empowering Future Cricket Stars: Join Our Winning Team!
          </p>
          <Link to="/programs">
            <Button size="lg" className="bg-white text-green-700 hover:bg-gray-100 px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:scale-105">
              Book Your Session Now
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
